/**
 * The catalog — category → service → plan → price (FR-C1).
 *
 * One read serves the whole screen: a services list that fetches a price per
 * row is a screen that flickers and a service that gets N+1 requests. The
 * price cell says *Price on request* rather than being blank, because that is
 * a real answer and it is where every plan an imported template starts.
 *
 * **Wave 15F** (C14 §10.6 items 1–2, ADR-0033): a list page. One table of every
 * service with its category as a column and a filter — the category cards it
 * replaced drew a table each — search, sort, paging; a row opens the service's
 * record. Adding a service and adding a category are dialogs (a list's add is a
 * dialog); the category filter lists every category, so one with nothing in it
 * yet is still there to pick.
 *
 * **C25 (Wave 27D, T-1.31, ADR-0054 §1–§5):** the catalogue is **Active ·
 * Inactive · Deleted**, three tabs over two reads (`live` and `deleted` — the
 * screen asks for the states it shows). Each service row has *Edit* (its
 * record), *Deactivate* / *Activate* and *Delete* / *Restore* (`moves.tsx`:
 * delete names what still uses it; a restore comes back inactive). The
 * categories sit in a table of their own under the services, in the same tab,
 * edited in `CategoryDialog` and moved by the same buttons — a category could
 * not be edited or retired at all before Wave 27. *Not on your website* marks
 * a service, and a plan, the firm keeps off its site; *Not offered* marks an
 * active service whose category is paused, by the service's own answer.
 * *Inactive* is the word the Branches screen set for a paused row.
 */

import { Alert, Badge, Button, Dialog, Input, Select, Tabs, buttonClass } from '@fiveox/ui'
import { Download, EyeOff, FolderPlus, Plus, Repeat } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'

import { DialogActions, ListPage, ListTable, ListToolbar, StatusBadge, useListTable } from '../../components/page'
import type { ListColumn } from '../../components/page'
import { SERVICE, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import {
  type Lifecycle,
  LIFECYCLE,
  LIFECYCLE_LABELS,
  type Plan,
  type Service,
  type ServiceCategory,
  appliesToLabel,
  planShown,
  priceLabel,
  services,
} from '../../lib/api/services'
import { CategoryDialog } from './CategoryDialog'
import { MoveButtons, useCatalogueMoves } from './moves'

function PriceCell({ plans }: { plans: Plan[] | undefined }) {
  const live = (plans ?? []).filter((plan) => plan.status !== 'deleted')
  if (live.length === 0) {
    return <span className="text-muted-foreground text-sm">No plans yet</span>
  }
  return (
    <ul className="max-w-[18rem] space-y-0.5 whitespace-normal">
      {live.map((plan) => (
        <li key={plan.id} className="text-sm" data-plan-cell={plan.id}>
          <span className="text-foreground">{plan.name}</span>{' '}
          <span className={plan.priced ? 'text-foreground' : 'text-muted-foreground'}>
            — {priceLabel(plan)}
          </span>
          {plan.status === 'inactive' && (
            <>
              {' '}
              <StatusBadge kind="catalogue" status="inactive" />
            </>
          )}
          {!planShown(plan) && (
            <>
              {' '}
              <Badge tone="neutral" size="sm">
                <EyeOff size={12} aria-hidden /> Not on your website
              </Badge>
            </>
          )}
        </li>
      ))}
    </ul>
  )
}

/** Add a service to a category — the old inline row, as a dialog. */
function ServiceDialog({
  open,
  categories,
  initialCategory,
  onClose,
  onSaved,
}: {
  open: boolean
  categories: ServiceCategory[]
  initialCategory: string
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const [name, setName] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setName('')
      setCategoryId(
        categories.some((c) => c.id === initialCategory) ? initialCategory : (categories[0]?.id ?? ''),
      )
      setError(null)
    }
  }, [open, initialCategory, categories])

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      await services.create({ category_id: categoryId, name })
      await onSaved()
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That service could not be added.')
    } finally {
      setBusy(false)
    }
  }

  const paused = categories.find((c) => c.id === categoryId)?.status === 'inactive'

  return (
    <Dialog open={open} onClose={onClose} title="Add a service" error={error} width="lg">
      <div className="grid gap-4 sm:grid-cols-[12rem_minmax(0,1fr)]">
        <Select
          label="Category"
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          hint={paused ? 'This category is inactive, so the service is not offered until it is active.' : undefined}
        >
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
              {category.status === 'inactive' ? ' (inactive)' : ''}
            </option>
          ))}
        </Select>
        <Input
          label="Service name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="GST return filing"
          hint="You set its plans, fees and workflow on the service’s own page."
        />
      </div>
      <DialogActions>
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={() => void save()} disabled={!name.trim() || !categoryId} loading={busy}>
          Add service
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const EMPTY_TAB: Record<Lifecycle, { services: string; categories: string }> = {
  active: { services: 'No active services.', categories: 'No active categories.' },
  inactive: {
    services: 'No inactive services. A service you deactivate waits here until you activate it.',
    categories: 'No inactive categories.',
  },
  deleted: {
    services: 'No deleted services. A service you delete waits here, and can be restored — it comes back inactive.',
    categories: 'No deleted categories.',
  },
}

export function Catalog() {
  const { can } = useEvaluation()
  const creatable = can(SERVICE, 'can_create')
  const editable = can(SERVICE, 'can_update')

  /** Every service in all three states, and every category. */
  const [rows, setRows] = useState<Service[]>([])
  const [categories, setCategories] = useState<ServiceCategory[]>([])
  const [tab, setTab] = useState<Lifecycle>('active')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [dialog, setDialog] = useState<'service' | null>(null)
  const [categoryDialog, setCategoryDialog] = useState<{ open: boolean; category: ServiceCategory | null }>({
    open: false,
    category: null,
  })

  /** w13b — ADR-0031 §5: a firm on `other` has no starting catalog, so the
   *  empty screen tells it to add its own rather than to import. */
  const [noTemplate, setNoTemplate] = useState(false)

  const load = useCallback(async () => {
    // The two states the staff list serves (I-5): `live` (active and
    // inactive) and `deleted`. Joined by id, so a service is listed once.
    const [live, deleted] = await Promise.all([services.list('live'), services.list('deleted')])
    const byId = new Map<string, Service>()
    for (const service of [...live.data.services, ...deleted.data.services]) byId.set(service.id, service)
    setRows([...byId.values()])
    setCategories(live.data.categories)
    setLoading(false)
    if (byId.size === 0) {
      void services
        .templates()
        .then((t) => setNoTemplate(t.data.profession === 'other'))
        .catch(() => undefined)
    }
  }, [])

  useEffect(() => {
    void load().catch(() => {
      setError('Your services could not be loaded.')
      setLoading(false)
    })
  }, [load])

  const moves = useCatalogueMoves({
    onMoved: async (sentence) => {
      setError(null)
      await load()
      setNotice(sentence)
    },
    onError: (sentence) => {
      setNotice(null)
      setError(sentence)
    },
  })

  const categoryOf = useCallback((id: string) => categories.find((c) => c.id === id), [categories])

  /** The categories a new service may go in: every one not deleted. */
  const liveCategories = useMemo(() => categories.filter((c) => c.status !== 'deleted'), [categories])

  const counts = useMemo(
    () =>
      Object.fromEntries(LIFECYCLE.map((state) => [state, rows.filter((row) => row.status === state).length])) as Record<
        Lifecycle,
        number
      >,
    [rows],
  )
  const inTab = useMemo(() => rows.filter((row) => row.status === tab), [rows, tab])

  const columns = useMemo<ListColumn<Service>[]>(
    () => [
      {
        key: 'name',
        label: 'Service',
        width: 220,
        required: true,
        phone: true,
        sortField: 'name',
        render: (service) => <span className="font-medium">{service.name}</span>,
      },
      {
        key: 'category',
        label: 'Category',
        width: 140,
        sortField: 'category_id',
        render: (service) => {
          const category = categoryOf(service.category_id)
          return (
            <span className="text-muted-foreground">
              {category?.name ?? '—'}
              {category && category.status !== 'active' && ` (${LIFECYCLE_LABELS[category.status].toLowerCase()})`}
            </span>
          )
        },
      },
      {
        key: 'for',
        label: 'For',
        width: 130,
        render: (service) => (
          <span className="text-muted-foreground">{appliesToLabel(service.applies_to)}</span>
        ),
      },
      {
        key: 'plans',
        label: 'Plans and fees',
        width: 260,
        render: (service) => <PriceCell plans={service.plans} />,
      },
      {
        key: 'flags',
        label: 'Notes',
        width: 170,
        phone: true,
        // What the old rows said beside the name — only the exceptions, the
        // one a firm must notice first, so it survives a phone's narrow cell.
        render: (service) => (
          <span className="flex flex-wrap gap-1.5">
            {service.status === 'active' && service.offered === false && (
              <Badge tone="warning" size="sm">
                Not offered — its category is inactive
              </Badge>
            )}
            {!service.site_visible && (
              <Badge tone="neutral" size="sm">
                <EyeOff size={12} aria-hidden /> Not on your website
              </Badge>
            )}
            {service.is_recurring && (
              <Badge tone="info" size="sm">
                <Repeat size={12} aria-hidden /> Recurring
              </Badge>
            )}
          </span>
        ),
      },
    ],
    [categoryOf],
  )

  const list = useListTable(inTab, {
    columns,
    search: (service) => [service.name, categoryOf(service.category_id)?.name],
    filters: [
      {
        key: 'category',
        label: 'Category',
        allLabel: 'Every category',
        options: categories.map((c) => ({
          value: c.id,
          label: c.status === 'active' ? c.name : `${c.name} (${LIFECYCLE_LABELS[c.status].toLowerCase()})`,
        })),
        match: (service, value) => service.category_id === value,
      },
      {
        key: 'site',
        label: 'On your website',
        allLabel: 'On the website or not',
        options: [
          { value: 'shown', label: 'Shown on your website' },
          { value: 'hidden', label: 'Not on your website' },
        ],
        match: (service, value) => (value === 'shown') === service.site_visible,
      },
    ],
    initialSort: { field: 'name' },
  })

  const categoryColumns = useMemo<ListColumn<ServiceCategory>[]>(
    () => [
      {
        key: 'name',
        label: 'Category',
        width: 180,
        required: true,
        phone: true,
        sortField: 'name',
        render: (category) => <span className="text-foreground font-medium">{category.name}</span>,
      },
      {
        key: 'description',
        label: 'Description',
        width: 240,
        render: (category) => (
          <span className="text-muted-foreground block max-w-xs truncate">{category.description || '—'}</span>
        ),
      },
      {
        key: 'order',
        label: 'Order',
        width: 70,
        sortField: 'display_order',
        render: (category) => <span className="text-muted-foreground">{category.display_order}</span>,
      },
      {
        key: 'services',
        label: 'Services',
        width: 90,
        phone: true,
        // Those not deleted — the count that refuses a category's delete.
        render: (category) => (
          <span className="text-muted-foreground">
            {rows.filter((row) => row.category_id === category.id && row.status !== 'deleted').length}
          </span>
        ),
      },
    ],
    [rows],
  )
  const categoriesInTab = useMemo(() => categories.filter((c) => c.status === tab), [categories, tab])
  const categoryList = useListTable(categoriesInTab, {
    columns: categoryColumns,
    initialSort: { field: 'display_order' },
  })

  const templateLink = creatable && (
    <Link to="/services/templates" className={buttonClass({ variant: 'outline' })}>
      <Download size={16} aria-hidden /> Start from a template
    </Link>
  )
  const addCategory = creatable && (
    <Button variant="outline" onClick={() => setCategoryDialog({ open: true, category: null })}>
      <FolderPlus size={16} aria-hidden /> Add a category
    </Button>
  )
  // A service needs a category to sit in; until one exists, adding a category
  // *is* the next step, and the button says why it waits.
  const addService = creatable && (
    <Button
      onClick={() => setDialog('service')}
      disabled={liveCategories.length === 0}
      title={liveCategories.length === 0 ? 'Add a category first — every service sits in one.' : undefined}
    >
      <Plus size={16} aria-hidden /> Add a service
    </Button>
  )

  const savedCategory = async (sentence: string) => {
    setError(null)
    await load()
    setNotice(sentence)
  }

  return (
    <>
      <ListPage
        title="Services"
        description="What your firm does, what each variant costs, and how a case on it moves."
        primary={
          creatable ? (
            <>
              {templateLink}
              {addCategory}
              {addService}
            </>
          ) : undefined
        }
        loading={loading}
        error={error}
        isEmpty={rows.length === 0 && categories.length === 0}
        empty={{
          title: 'Your catalog is empty',
          description: noTemplate
            ? 'ProDesk ships no starting list for your kind of practice, so add your own services: a category first, then what you offer in it.'
            : 'You can add services one at a time, or start from the template for your profession — nothing is created until you choose.',
          action: creatable ? (noTemplate ? addCategory : templateLink) : undefined,
        }}
        toolbar={
          <div className="space-y-4">
            <Tabs
              idBase="catalogue"
              active={tab}
              onChange={(id) => setTab(id as Lifecycle)}
              tabs={LIFECYCLE.map((state) => ({ id: state, label: LIFECYCLE_LABELS[state], badge: counts[state] }))}
            />
            <ListToolbar list={list} placeholder="Search by service or category" />
          </div>
        }
      >
        {notice && !error && <Alert tone="success">{notice}</Alert>}
        <ListTable
          list={list}
          rowKey={(service) => service.id}
          rowHref={(service) => `/services/${service.id}`}
          empty={EMPTY_TAB[tab].services}
          trailing={
            editable
              ? {
                  header: <span className="sr-only">Actions</span>,
                  width: 250,
                  cell: (service) => (
                    <MoveButtons
                      compact
                      target={{ kind: 'service', id: service.id, name: service.name, status: service.status }}
                      moves={moves}
                      edit={`/services/${service.id}`}
                    />
                  ),
                }
              : undefined
          }
        />

        <section aria-labelledby="catalogue-categories" className="space-y-3 pt-4" data-categories="">
          <div>
            <h2 id="catalogue-categories" className="text-foreground text-base font-semibold">
              Categories
            </h2>
            <p className="text-muted-foreground text-sm">
              The headings your services sit under, in your order — on your website and in every list. An inactive
              category takes every service in it off offer.
            </p>
          </div>
          <ListTable
            list={categoryList}
            rowKey={(category) => category.id}
            empty={EMPTY_TAB[tab].categories}
            trailing={
              editable
                ? {
                    header: <span className="sr-only">Actions</span>,
                    width: 250,
                    cell: (category) => (
                      <MoveButtons
                        compact
                        target={{ kind: 'category', id: category.id, name: category.name, status: category.status }}
                        moves={moves}
                        edit={() => setCategoryDialog({ open: true, category })}
                      />
                    ),
                  }
                : undefined
            }
          />
        </section>
      </ListPage>

      <CategoryDialog
        open={categoryDialog.open}
        category={categoryDialog.category}
        onClose={() => setCategoryDialog({ open: false, category: null })}
        onSaved={savedCategory}
      />
      <ServiceDialog
        open={dialog === 'service'}
        categories={liveCategories}
        initialCategory={list.filters.find((f) => f.def.key === 'category')?.value ?? ''}
        onClose={() => setDialog(null)}
        onSaved={load}
      />
      {moves.dialog}
    </>
  )
}
